/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.patients

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.ListItem
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import studio.oddpods.cauce.core.base.designsystem.core.TopAppBarAction
import studio.oddpods.cauce.core.base.ui.EventsEffect
import studio.oddpods.cauce.core.model.session.PaymentStatus
import studio.oddpods.cauce.core.model.session.RecurrenceFrequency
import studio.oddpods.cauce.core.model.session.SessionType
import studio.oddpods.cauce.core.ui.SessionCardContent
import studio.oddpods.cauce.core.ui.asString
import studio.oddpods.cauce.core.ui.scaffold.FloatingActionButtonContent
import studio.oddpods.cauce.core.ui.scaffold.KptScaffold
import studio.oddpods.cauce.feature.patients.generated.resources.Res
import studio.oddpods.cauce.feature.patients.generated.resources.feature_patients_send_reminder
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_create_recurring_schedule
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_delete
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_delete_cancel
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_delete_confirm
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_delete_confirm_message
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_delete_confirm_title
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_edit_action
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_fee
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_inactive_banner
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_log_session
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_no_sessions
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_owed
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_partial_amount
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_phone
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_recurring_schedules
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_register_payment
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_session_item
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_sessions
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_sessions_count
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_status_paid
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_status_partial
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_status_pending
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_status_waived
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_stop_schedule_confirm
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_stop_schedule_message
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_stop_schedule_title
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_this_month
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_title
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_type_double
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_type_final
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_type_first
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_type_regular
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_waive_cancel
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_waive_confirm
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_waive_message
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_waive_title
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_frequency_biweekly
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_frequency_custom
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_frequency_monthly
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_frequency_weekly

@Suppress("CyclomaticComplexMethod", "LongMethod")
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PatientDetailScreen(
    patientId: String,
    onBackClick: () -> Unit,
    onLogSession: (String) -> Unit,
    modifier: Modifier = Modifier,
    onSendReminder: (String) -> Unit = {},
    onEditPatient: (String) -> Unit = {},
    onCreateRecurringSchedule: (String) -> Unit = {},
    onEditRecurringSchedule: (String, String) -> Unit = { _, _ -> },
    onSessionClick: (String) -> Unit = {},
    viewModel: PatientDetailViewModel = koinViewModel { parametersOf(patientId) },
) {
    val state by viewModel.stateFlow.collectAsStateWithLifecycle()
    var showDeleteDialog by remember { mutableStateOf(false) }
    var showWaiveDialog by remember { mutableStateOf(false) }
    var showActionSheet by remember { mutableStateOf(false) }
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)

    EventsEffect(eventFlow = viewModel.eventFlow) { event ->
        when (event) {
            is PatientDetailEvent.NavigateToLogSession -> onLogSession(event.patientId)
            is PatientDetailEvent.NavigateToSendReminder -> onSendReminder(event.patientId)
            is PatientDetailEvent.NavigateToEditPatient -> onEditPatient(event.patientId)
            is PatientDetailEvent.NavigateToCreateRecurringSchedule -> onCreateRecurringSchedule(event.patientId)
            is PatientDetailEvent.PatientDeactivated -> onBackClick()
        }
    }

    if (showDeleteDialog) {
        DeletePatientDialog(
            onConfirm = {
                showDeleteDialog = false
                if (state.totalOwed > 0) {
                    showWaiveDialog = true
                } else {
                    viewModel.trySendAction(PatientDetailAction.DeactivateClick)
                }
            },
            onDismiss = { showDeleteDialog = false },
        )
    }

    if (showWaiveDialog) {
        WaiveBalanceDialog(
            totalOwedDisplay = state.totalOwedDisplay,
            onWaive = {
                showWaiveDialog = false
                viewModel.trySendAction(PatientDetailAction.DeactivateAndWaiveClick)
            },
            onDismiss = { showWaiveDialog = false },
        )
    }

    state.stopScheduleDialog?.let {
        StopScheduleDialog(
            onConfirm = { viewModel.trySendAction(PatientDetailAction.ConfirmStopSchedule) },
            onDismiss = { viewModel.trySendAction(PatientDetailAction.DismissStopScheduleDialog) },
        )
    }

    state.markPaidDialog?.let { dialog ->
        MarkAsPaidDialog(
            dialog = dialog,
            isMarking = state.markingPaid,
            onToggleSession = { sessionId ->
                viewModel.trySendAction(PatientDetailAction.MarkPaidSessionToggled(sessionId))
            },
            onMethodChange = { method ->
                viewModel.trySendAction(PatientDetailAction.MarkPaidMethodChanged(method))
            },
            onConfirm = { viewModel.trySendAction(PatientDetailAction.ConfirmMarkPaid) },
            onDismiss = { viewModel.trySendAction(PatientDetailAction.DismissMarkPaidDialog) },
        )
    }

    if (showActionSheet) {
        ModalBottomSheet(
            onDismissRequest = { showActionSheet = false },
            sheetState = sheetState,
        ) {
            ListItem(
                headlineContent = {
                    Text(stringResource(Res.string.patients_detail_log_session))
                },
                modifier = Modifier.clickable {
                    showActionSheet = false
                    viewModel.trySendAction(PatientDetailAction.LogSessionClick)
                },
            )
            ListItem(
                headlineContent = {
                    Text(stringResource(Res.string.patients_detail_create_recurring_schedule))
                },
                modifier = Modifier.clickable {
                    showActionSheet = false
                    viewModel.trySendAction(PatientDetailAction.CreateRecurringScheduleClick)
                },
            )
            ListItem(
                headlineContent = {
                    Text(stringResource(Res.string.patients_detail_register_payment))
                },
                modifier = Modifier.clickable {
                    showActionSheet = false
                    viewModel.trySendAction(PatientDetailAction.RegisterPaymentClick)
                },
            )
            Spacer(modifier = Modifier.height(16.dp))
        }
    }

    val isActive = state.patient?.active != false

    KptScaffold(
        onNavigationIconClick = onBackClick,
        title = stringResource(Res.string.patients_detail_title),
        floatingActionButtonContent = if (!state.isLoading && isActive) {
            FloatingActionButtonContent(
                onClick = { showActionSheet = true },
                contentColor = MaterialTheme.colorScheme.onPrimaryContainer,
                content = { Icon(Icons.Default.Add, contentDescription = null) },
            )
        } else {
            null
        },
        actions = if (isActive) {
            listOf(
                TopAppBarAction(
                    icon = Icons.Filled.Edit,
                    contentDescription = stringResource(Res.string.patients_detail_edit_action),
                    onClick = { viewModel.trySendAction(PatientDetailAction.EditClick) },
                ),
                TopAppBarAction(
                    icon = Icons.Default.Delete,
                    contentDescription = stringResource(Res.string.patients_detail_delete),
                    onClick = { showDeleteDialog = true },
                ),
            )
        } else {
            emptyList()
        },
        modifier = modifier,
    ) {
        when {
            state.isLoading -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    CircularProgressIndicator()
                }
            }
            state.error != null -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(state.error!!.asString(), color = MaterialTheme.colorScheme.error)
                }
            }
            state.patient != null -> {
                PatientDetailContent(
                    state = state,
                    onAction = viewModel::trySendAction,
                    onEditRecurringSchedule = onEditRecurringSchedule,
                    onSessionClick = onSessionClick,
                )
            }
        }
    }
}

@Composable
private fun PatientDetailContent(
    state: PatientDetailState,
    onAction: (PatientDetailAction) -> Unit,
    onEditRecurringSchedule: (String, String) -> Unit,
    onSessionClick: (String) -> Unit,
) {
    val patient = state.patient!!
    LazyColumn(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = 16.dp),
    ) {
        item {
            Spacer(modifier = Modifier.height(8.dp))
            Card(modifier = Modifier.fillMaxWidth()) {
                Column(modifier = Modifier.padding(16.dp)) {
                    Text(
                        text = patient.name,
                        style = MaterialTheme.typography.titleLarge,
                    )
                    Text(
                        text = stringResource(Res.string.patients_detail_phone, patient.phone),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                    Text(
                        text = stringResource(
                            Res.string.patients_detail_fee,
                            state.defaultFeeDisplay,
                        ),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
            if (!patient.active) {
                Spacer(modifier = Modifier.height(8.dp))
                Card(
                    colors = CardDefaults.cardColors(
                        containerColor = MaterialTheme.colorScheme.errorContainer,
                    ),
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text(
                        text = stringResource(Res.string.patients_detail_inactive_banner),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onErrorContainer,
                        modifier = Modifier.padding(12.dp),
                    )
                }
            }
            Spacer(modifier = Modifier.height(12.dp))
        }

        item {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                StatCard(
                    label = stringResource(Res.string.patients_detail_owed),
                    value = state.totalOwedDisplay,
                    valueColor = if (state.totalOwed > 0) {
                        MaterialTheme.colorScheme.error
                    } else {
                        MaterialTheme.colorScheme.primary
                    },
                    modifier = Modifier.weight(1f),
                )
                StatCard(
                    label = stringResource(Res.string.patients_detail_this_month),
                    value = stringResource(
                        Res.string.patients_detail_sessions_count,
                        state.sessionsThisMonth,
                    ),
                    modifier = Modifier.weight(1f),
                )
            }
            Spacer(modifier = Modifier.height(16.dp))
        }

        if (patient.active && state.hasPendingReminder) {
            item {
                FilledTonalButton(
                    onClick = { onAction(PatientDetailAction.SendReminderClick) },
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text(stringResource(Res.string.feature_patients_send_reminder))
                }
                Spacer(modifier = Modifier.height(20.dp))
            }
        }

        if (state.activeSchedules.isNotEmpty()) {
            item {
                Text(
                    text = stringResource(Res.string.patients_detail_recurring_schedules),
                    style = MaterialTheme.typography.titleMedium,
                )
                Spacer(modifier = Modifier.height(8.dp))
            }
            items(state.activeSchedules) { display ->
                ScheduleCard(
                    display = display,
                    onClick = {
                        onEditRecurringSchedule(
                            display.schedule.id,
                            state.patient?.name ?: "",
                        )
                    },
                    onStopClick = {
                        onAction(PatientDetailAction.StopScheduleClick(display.schedule.id))
                    },
                )
                Spacer(modifier = Modifier.height(8.dp))
            }
            item {
                Spacer(modifier = Modifier.height(12.dp))
            }
        }

        item {
            Text(
                text = stringResource(Res.string.patients_detail_sessions),
                style = MaterialTheme.typography.titleMedium,
            )
            Spacer(modifier = Modifier.height(8.dp))
        }

        if (state.sessionsDisplay.isEmpty()) {
            item {
                Text(
                    text = stringResource(Res.string.patients_detail_no_sessions),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        } else {
            items(state.sessionsDisplay) { display ->
                SessionCard(
                    display = display,
                    onClick = { onSessionClick(display.session.id) },
                )
                Spacer(modifier = Modifier.height(8.dp))
            }
        }

        item {
            Spacer(modifier = Modifier.height(16.dp))
        }
    }
}

@Composable
private fun DeletePatientDialog(
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(Res.string.patients_detail_delete_confirm_title)) },
        text = { Text(stringResource(Res.string.patients_detail_delete_confirm_message)) },
        confirmButton = {
            TextButton(onClick = onConfirm) {
                Text(
                    stringResource(Res.string.patients_detail_delete_confirm),
                    color = MaterialTheme.colorScheme.error,
                )
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(stringResource(Res.string.patients_detail_delete_cancel))
            }
        },
    )
}

@Composable
private fun WaiveBalanceDialog(
    totalOwedDisplay: String,
    onWaive: () -> Unit,
    onDismiss: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(Res.string.patients_detail_waive_title)) },
        text = {
            Text(stringResource(Res.string.patients_detail_waive_message, totalOwedDisplay))
        },
        confirmButton = {
            TextButton(onClick = onWaive) {
                Text(
                    stringResource(Res.string.patients_detail_waive_confirm),
                    color = MaterialTheme.colorScheme.error,
                )
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(stringResource(Res.string.patients_detail_waive_cancel))
            }
        },
    )
}

@Composable
private fun StatCard(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    valueColor: androidx.compose.ui.graphics.Color = MaterialTheme.colorScheme.onSurface,
) {
    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceVariant,
        ),
    ) {
        Column(modifier = Modifier.padding(12.dp)) {
            Text(
                text = label,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Text(
                text = value,
                style = MaterialTheme.typography.titleMedium,
                color = valueColor,
            )
        }
    }
}

@Suppress("CyclomaticComplexMethod")
@Composable
private fun SessionCard(display: SessionDisplay, onClick: () -> Unit) {
    val session = display.session
    val typeLabel = stringResource(
        when (session.sessionType) {
            SessionType.REGULAR -> Res.string.patients_detail_type_regular
            SessionType.FIRST -> Res.string.patients_detail_type_first
            SessionType.FINAL -> Res.string.patients_detail_type_final
            SessionType.DOUBLE -> Res.string.patients_detail_type_double
        },
    )
    val statusLabel = stringResource(
        when (session.paymentStatus) {
            PaymentStatus.PAID -> Res.string.patients_detail_status_paid
            PaymentStatus.PENDING -> Res.string.patients_detail_status_pending
            PaymentStatus.PARTIAL -> Res.string.patients_detail_status_partial
            PaymentStatus.WAIVED -> Res.string.patients_detail_status_waived
        },
    )
    val detailText = when (session.paymentStatus) {
        PaymentStatus.PARTIAL -> stringResource(
            Res.string.patients_detail_partial_amount,
            display.amountPaidDisplay,
            display.feeDisplay,
        )
        else -> stringResource(
            Res.string.patients_detail_session_item,
            typeLabel,
            display.feeDisplay,
        )
    }
    val statusColor = when (session.paymentStatus) {
        PaymentStatus.PAID -> MaterialTheme.colorScheme.primary
        PaymentStatus.PENDING -> MaterialTheme.colorScheme.tertiary
        PaymentStatus.PARTIAL -> MaterialTheme.colorScheme.tertiary
        PaymentStatus.WAIVED -> MaterialTheme.colorScheme.onSurfaceVariant
    }
    Card(onClick = onClick, modifier = Modifier.fillMaxWidth()) {
        SessionCardContent(
            sessionDate = display.dateDisplay,
            detailText = detailText,
            statusLabel = statusLabel,
            statusColor = statusColor,
        )
    }
}

@Composable
private fun StopScheduleDialog(
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(Res.string.patients_detail_stop_schedule_title)) },
        text = { Text(stringResource(Res.string.patients_detail_stop_schedule_message)) },
        confirmButton = {
            TextButton(onClick = onConfirm) {
                Text(
                    stringResource(Res.string.patients_detail_stop_schedule_confirm),
                    color = MaterialTheme.colorScheme.error,
                )
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(stringResource(Res.string.patients_detail_delete_cancel))
            }
        },
    )
}

@Composable
private fun ScheduleCard(
    display: RecurringScheduleDisplay,
    onClick: () -> Unit,
    onStopClick: () -> Unit,
) {
    val frequencyText = when (display.schedule.frequency) {
        RecurrenceFrequency.WEEKLY -> stringResource(Res.string.patients_session_frequency_weekly)
        RecurrenceFrequency.BIWEEKLY -> stringResource(Res.string.patients_session_frequency_biweekly)
        RecurrenceFrequency.MONTHLY -> stringResource(Res.string.patients_session_frequency_monthly)
        RecurrenceFrequency.CUSTOM -> stringResource(
            Res.string.patients_session_frequency_custom,
        )
    }
    Card(onClick = onClick, modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.padding(start = 16.dp, top = 16.dp, bottom = 16.dp, end = 8.dp)
                .fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = frequencyText,
                    style = MaterialTheme.typography.bodyLarge,
                )
                Text(
                    text = display.feeDisplay,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            IconButton(onClick = onStopClick) {
                Icon(
                    imageVector = Icons.Default.Delete,
                    contentDescription = stringResource(Res.string.patients_detail_stop_schedule_confirm),
                    tint = MaterialTheme.colorScheme.error,
                )
            }
        }
    }
}
